import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { postChatCompletion } from '../api/data-connectors/chat-completion'
import { failureTypeFromError, sendTelemetryEvent } from '../api/data-connectors/telemetry'
import { addCommentReplies, insertReviewComments } from '../api/word/word-service'
import { STREAMING_ENABLED } from '../constants/chat'
import { SYSTEM_PROMPT } from '../constants/system-prompt'
import { TELEMETRY_ENABLED } from '../constants/telemetry'
import type { ApiChatMessage, ChatMessage, ModelKey, ReviewCommentItem, ReviewMode } from '../types'
import { DEFAULT_MODEL } from '../types'
import type { TelemetryTask } from '../types/telemetry'
import {
  buildInsertReviewCommentsOptions,
  partitionReviewCommentItems,
} from '../utils/word/review-comments'
import { senseReviewMode } from '../utils/sense-review-mode'
import { useAuth } from './useAuth'
import { useChatContext } from './useChatContext'
import { useChatMessages } from './useChatMessages'
import { useKeepMessageOnSend } from './useKeepMessageOnSend'
import { useNotification } from './useNotification'

/**
 * Builds index-aligned Word comment ids for View links in the summary bubble.
 *
 * The API returns a single ordered list (insert / reply / skipped), but Word operations run
 * in two batches (all inserts, then all replies), so insertIds / replyIds are only indexed by
 * their own batch position, not by summary segment index. We walk items in API order and map
 * each segment back to the right id: next insert id, next reply id, or null for skipped.
 */
function buildWordCommentIds(
  items: ReviewCommentItem[],
  insertIds: (string | null)[],
  replyIds: (string | null)[]
): (string | null)[] {
  let insertIndex = 0
  let replyIndex = 0

  return items.map((item) => {
    if (item.kind === 'skipped') return null
    if (item.kind === 'reply') {
      const id = replyIds[replyIndex] ?? null
      replyIndex++
      return id
    }
    const id = insertIds[insertIndex] ?? null
    insertIndex++
    return id
  })
}

/**
 * What happened when review comments were written into Word.
 * 'none' means there was nothing to write. 'failed' covers Word refusing them and none landing.
 */
type CommentsOutcome = 'applied' | 'failed' | 'none'

interface SendMessageVariables {
  content: string
  model: ModelKey
  reviewMode?: ReviewMode | null
  isCommentMode?: boolean
  includeComments?: boolean
  systemPrompt?: string
  requiresFullDocument: boolean
  historySnapshot: ApiChatMessage[]
  telemetryTask: TelemetryTask
  startedAt: number
}

/**
 * Coordinates chat send lifecycle with optimistic UI state.
 *
 * - Optimistically adds the user message
 * - Builds request context and auth token
 * - Sends chat completion request (streaming or non-streaming)
 * - Commits assistant output on success
 * - Rolls back optimistic state on failure/cancel as needed
 *
 * Prevents concurrent sends while a request is in flight.
 */
export const useSendMessage = () => {
  const [selectedModel, setSelectedModel] = useState<ModelKey>(DEFAULT_MODEL)
  const [reviewMode, setReviewMode] = useState<ReviewMode | null>(null)
  const [isCommentMode, setIsCommentMode] = useState(false)
  // Independent of review mode: user can include existing comments as context
  // without activating review mode (AI reads threads but won't insert new comments).
  // Auto-activates when review/comment mode turns on; auto-clears when both turn off.
  // User can override the badge in either direction while a mode is active.
  const [includeComments, setIncludeComments] = useState(false)
  const [isContextBarExpanded, setIsContextBarExpanded] = useState(true)
  const [systemPrompt, setSystemPrompt] = useState('')
  const [draftMessage, setDraftMessageState] = useState('')
  const isCommentModeRef = useRef(isCommentMode)
  isCommentModeRef.current = isCommentMode
  const systemPromptRef = useRef(systemPrompt)
  systemPromptRef.current = systemPrompt
  const { keepMessageOnSend, setKeepMessageOnSend } = useKeepMessageOnSend()
  // Stored as a ref because it's a UI-only flag that never reaches the API — it only needs
  // to survive from prompt selection to the next send so it can be stamped into sendContext.
  const requiresFullDocumentRef = useRef(false)
  const setRequiresFullDocument = useCallback((value: boolean) => {
    requiresFullDocumentRef.current = value
  }, [])

  const setDraftMessage = useCallback(
    (value: string) => {
      // Clears QC mode and system prompt whenever the editor empties (send, manual clear, etc.)
      if (!value) {
        if (systemPromptRef.current && isCommentModeRef.current) {
          // If there is a system prompt, we assume the user used a prompt before
          setIsCommentMode(false)
        }
        setReviewMode(null)
        setSystemPrompt('')
        setRequiresFullDocument(false)
        // user visible state remains and is up to the user to clear if they want to
      }
      setDraftMessageState(value)
    },
    [setRequiresFullDocument]
  )

  const { messages, addMessage, updateMessage, deleteMessage, clearMessages } = useChatMessages()
  const { notify } = useNotification()
  const { t } = useTranslation()
  const undoControllerRef = useRef<AbortController | null>(null)
  const streamCancelledRef = useRef(false)
  // Tracks last sent content + full send state snapshot for undo (restores UI after undoSend)
  const lastSendRef = useRef<{
    content: string
    messageId: string
    reviewMode: ReviewMode | null
    systemPrompt: string
    requiresFullDocument: boolean
  } | null>(null)
  const assistantMessageId = useRef<string | null>(null)
  const { buildContext, getMouseSelectionParagraphIds } = useChatContext()
  const auth = useAuth()

  useEffect(() => {
    return () => {
      streamCancelledRef.current = true
      undoControllerRef.current?.abort()
    }
  }, [])

  const resetStreamTracking = () => {
    assistantMessageId.current = null
    streamCancelledRef.current = false
  }

  const clearLastSend = useCallback(() => {
    lastSendRef.current = null
  }, [])

  // Shared by both sendBlocking and sendStreaming: apply thread replies and new
  // anchored comments, then annotate the bubble with a combined infoNote.
  //
  // Replies and new comments are independent Word operations (no shared anchor
  // resolution), so each runs and fails on its own - one Word API error does not
  // block the other from applying. Any review mode can return either or both in
  // the same response (see comment-reply architecture plan), so this is not
  // gated on reviewMode.
  const applyReviewComments = useCallback(
    async (
      result: { comments?: ReviewCommentItem[] },
      messageId: string | null | undefined,
      reviewMode?: ReviewMode | null
    ): Promise<CommentsOutcome> => {
      const items = result.comments ?? []
      if (items.length === 0) return 'none'

      const { inserts, replies } = partitionReviewCommentItems(items)
      let insertedCount = 0
      let hadWordApiFailure = false

      let replyCommentIds: (string | null)[] = []
      let insertCommentIds: (string | null)[] = []

      if (replies.length) {
        try {
          const replyResult = await addCommentReplies(replies)
          insertedCount += replyResult.insertedCount
          replyCommentIds = replyResult.commentIds
        } catch {
          hadWordApiFailure = true
        }
      }

      if (inserts.length) {
        const isMouseSelectionReview = reviewMode === 'mouse_selection_comments'

        try {
          const insertResult = await insertReviewComments(
            inserts,
            buildInsertReviewCommentsOptions({
              resolveDocumentWide: Boolean(reviewMode) && !isMouseSelectionReview,
              selectionParagraphIds: isMouseSelectionReview ? getMouseSelectionParagraphIds() : [],
            })
          )
          insertedCount += insertResult.insertedCount
          insertCommentIds = insertResult.commentIds
        } catch {
          hadWordApiFailure = true
        }
      }

      const wordCommentIds = buildWordCommentIds(items, insertCommentIds, replyCommentIds)

      if (hadWordApiFailure) {
        // Message is already shown; surface a targeted error toast and do NOT
        // rethrow so the mutation stays in its success state.
        notify({ isSuccess: false, text: t('chat.commentsInsertFailed') })
      }

      const messageUpdates: Partial<Pick<ChatMessage, 'wordCommentIds' | 'infoNote'>> = {}

      if (messageId && wordCommentIds?.some((commentId) => commentId !== null)) {
        messageUpdates.wordCommentIds = wordCommentIds
      }

      if (messageId && insertedCount > 0) {
        messageUpdates.infoNote = t('chat.commentsAdded', { count: insertedCount })
      }

      if (messageId && Object.keys(messageUpdates).length > 0) {
        updateMessage(messageId, messageUpdates)
      }

      // Comments that all point at paragraphs or threads that no longer exist land nowhere
      // without Word throwing. The user got no comments, so that is a failed review too.
      const hadCommentsToApply = inserts.length + replies.length > 0
      const noneLanded = hadCommentsToApply && insertedCount === 0
      if (hadWordApiFailure || noneLanded) return 'failed'
      return insertedCount > 0 ? 'applied' : 'none'
    },
    [updateMessage, t, notify, getMouseSelectionParagraphIds]
  )

  const sendBlocking = useCallback(
    async ({
      apiMessages,
      model,
      context,
      reviewMode,
      undoSignal,
      authToken,
      telemetryTask,
    }: {
      apiMessages: ApiChatMessage[]
      model: ModelKey
      context: Awaited<ReturnType<typeof buildContext>>
      reviewMode?: ReviewMode | null
      undoSignal: AbortSignal
      authToken?: string
      telemetryTask: TelemetryTask
    }): Promise<CommentsOutcome> => {
      const data = await postChatCompletion({
        messages: apiMessages,
        model,
        context,
        reviewMode,
        undoSignal,
        streaming: false,
        authToken,
      })

      if (typeof data.content !== 'string') return 'none'

      const messageId = addMessage(data.content, 'assistant')
      if (TELEMETRY_ENABLED) {
        updateMessage(messageId, { telemetryTask })
      }

      updateMessage(messageId, { sources: data.sources })

      if (data.comments?.some((item) => item.kind !== 'skipped')) {
        return applyReviewComments(data, messageId, reviewMode)
      }
      return 'none'
    },
    [addMessage, updateMessage, applyReviewComments]
  )

  const sendStreaming = useCallback(
    async ({
      apiMessages,
      model,
      context,
      reviewMode,
      undoSignal,
      authToken,
      telemetryTask,
    }: {
      apiMessages: ApiChatMessage[]
      model: ModelKey
      context: Awaited<ReturnType<typeof buildContext>>
      reviewMode?: ReviewMode | null
      undoSignal: AbortSignal
      authToken?: string
      telemetryTask: TelemetryTask
    }): Promise<CommentsOutcome> => {
      const shouldIgnoreStreamCallbacks = () => streamCancelledRef.current || undoSignal.aborted
      const PREVIEW_TICK_MS = 40
      const PREVIEW_INITIAL_BUFFER_CHARS = 120
      const PREVIEW_MIN_CHARS_PER_TICK = 18
      const PREVIEW_MAX_CHARS_PER_TICK = 64
      let previewQueue = ''
      let renderedPreview = ''
      let previewInterval: ReturnType<typeof setInterval> | null = null

      const ensureAssistantPlaceholder = () => {
        if (shouldIgnoreStreamCallbacks()) {
          return null
        }

        if (assistantMessageId.current) {
          return assistantMessageId.current
        }

        const messageId = addMessage('', 'assistant', 'streaming')
        assistantMessageId.current = messageId

        return messageId
      }

      const stopPreviewInterval = () => {
        if (previewInterval === null) return
        clearInterval(previewInterval)
        previewInterval = null
      }

      const flushQueuedPreview = (force = false) => {
        if (!previewQueue || shouldIgnoreStreamCallbacks()) {
          return
        }

        const activeAssistantMessageId = ensureAssistantPlaceholder()
        if (!activeAssistantMessageId) {
          return
        }

        const revealCount = force
          ? previewQueue.length
          : Math.min(
              previewQueue.length,
              Math.max(
                PREVIEW_MIN_CHARS_PER_TICK,
                Math.min(PREVIEW_MAX_CHARS_PER_TICK, Math.ceil(previewQueue.length / 6))
              )
            )

        renderedPreview += previewQueue.slice(0, revealCount)
        previewQueue = previewQueue.slice(revealCount)

        updateMessage(activeAssistantMessageId, {
          streamPreviewContent: renderedPreview,
          streamState: 'streaming',
        })
      }

      const ensurePreviewInterval = () => {
        if (previewInterval !== null) return

        previewInterval = setInterval(() => {
          if (shouldIgnoreStreamCallbacks()) {
            stopPreviewInterval()
            return
          }

          const hasStartedRendering = renderedPreview.length > 0
          const shouldWaitForInitialBuffer =
            !hasStartedRendering && previewQueue.length < PREVIEW_INITIAL_BUFFER_CHARS

          if (shouldWaitForInitialBuffer) {
            return
          }

          flushQueuedPreview(false)
        }, PREVIEW_TICK_MS)
      }

      const finalizeStreamingAssistant = (
        finalContent: string,
        sources: ChatMessage['sources']
      ) => {
        if (!assistantMessageId.current) {
          throw new Error('Streaming contract violated: no chunks received before completion')
        }

        updateMessage(assistantMessageId.current, {
          content: finalContent,
          streamState: 'done',
          streamPreviewContent: undefined,
          sources,
        })
      }

      try {
        const data = await postChatCompletion({
          messages: apiMessages,
          model,
          context,
          reviewMode,
          undoSignal,
          streaming: true,
          authToken,
          onChunk: (chunk) => {
            if (shouldIgnoreStreamCallbacks()) {
              return
            }

            if (!ensureAssistantPlaceholder()) {
              return
            }

            previewQueue += chunk
            ensurePreviewInterval()
          },
        })

        if (shouldIgnoreStreamCallbacks()) {
          return 'none'
        }

        flushQueuedPreview(true)

        // Controlled API contract: streaming responses always emit chunk callbacks.
        if (typeof data.content === 'string') {
          finalizeStreamingAssistant(data.content, data.sources)
        }

        // Messages are kept in local storage. A task saved while telemetry is off would show up
        // later as an Insert for a task the backend never saw start.
        if (TELEMETRY_ENABLED && assistantMessageId.current) {
          updateMessage(assistantMessageId.current, { telemetryTask })
        }

        // Most likely truncation error: show a warning toast but keep the bubble as-is.
        if (data.isFinishError) {
          notify({ isSuccess: false, text: t('chat.responseTruncated') })
        }
        if (data.comments?.some((item) => item.kind !== 'skipped')) {
          return await applyReviewComments(data, assistantMessageId.current, reviewMode)
        }
        return 'none'
      } catch (error) {
        if (shouldIgnoreStreamCallbacks()) {
          throw error
        }

        const activeAssistantMessageId = assistantMessageId.current
        if (activeAssistantMessageId) {
          updateMessage(activeAssistantMessageId, {
            content: `<p><em>${t('chat.sendFailure')}</em></p>`,
            streamState: 'error',
            streamPreviewContent: undefined,
          })
        }
        throw error
      } finally {
        stopPreviewInterval()
      }
    },
    [addMessage, updateMessage, t, notify, applyReviewComments]
  )

  const mutation = useMutation<CommentsOutcome, Error, SendMessageVariables, { messageId: string }>(
    {
      mutationFn: async ({
        content,
        model,
        reviewMode,
        isCommentMode,
        includeComments,
        systemPrompt,
        historySnapshot,
        telemetryTask,
      }: SendMessageVariables) => {
        streamCancelledRef.current = false
        undoControllerRef.current?.abort()
        undoControllerRef.current = new AbortController()

        const apiMessages: ApiChatMessage[] = [
          { role: 'system', content: SYSTEM_PROMPT },
          ...historySnapshot,
          {
            role: 'user',
            content: systemPrompt ? `${content}\n\n${systemPrompt}` : content,
          },
        ]

        // either we got review mode from standard prompt, then it should have set the context,
        // or we got review mode from isCommentMode, then we need to sense the context and set the review mode
        const context = await buildContext({
          isCommentMode: Boolean(isCommentMode) || Boolean(reviewMode),
          includeComments: includeComments ?? false,
        })

        // Prompt review mode wins; otherwise sense from context only in comment mode.
        const currentReviewMode = reviewMode ?? (isCommentMode ? senseReviewMode(context) : null)

        const authToken = await auth.getAccessToken()
        const undoSignal = undoControllerRef.current.signal

        const send = STREAMING_ENABLED ? sendStreaming : sendBlocking
        return send({
          apiMessages,
          model,
          context,
          reviewMode: currentReviewMode ?? null,
          undoSignal,
          authToken: authToken ?? undefined,
          telemetryTask,
        })
      },
      onMutate: ({
        content,
        model,
        reviewMode,
        systemPrompt,
        requiresFullDocument,
        telemetryTask,
      }: SendMessageVariables) => {
        sendTelemetryEvent({
          event_type: 'task_started',
          task_id: telemetryTask.taskId,
          task_type: telemetryTask.taskType,
          model_version: model,
        })

        const sendContext = { model, reviewMode, systemPrompt, requiresFullDocument }
        const messageId = addMessage(content, 'user', 'done', sendContext)
        lastSendRef.current = {
          content,
          messageId,
          reviewMode: reviewMode ?? null,
          systemPrompt: systemPrompt ?? '',
          requiresFullDocument,
          // requiresDocumentComments is not stored here as it would only add more complexity.
        }
        return { messageId }
      },
      onSuccess: (commentsOutcome, { telemetryTask, model, startedAt }) => {
        // An undo or a closed task pane can still land here when the answer had already
        // arrived. Neither is a success, and a cancel has no event type yet, so send nothing.
        const wasCancelled = undoControllerRef.current?.signal.aborted
        if (!wasCancelled) {
          const task = {
            task_id: telemetryTask.taskId,
            task_type: telemetryTask.taskType,
            model_version: model,
            duration_ms: Date.now() - startedAt,
          }
          // Review comments are the point of a review request: if Word refused them,
          // the user saw an error, even though the API answered fine.
          if (commentsOutcome === 'failed') {
            sendTelemetryEvent({ ...task, event_type: 'task_failed', failure_type: 'word_error' })
          } else {
            const taskOutcome = commentsOutcome === 'applied' ? 'comments_applied' : 'responded'
            sendTelemetryEvent({ ...task, event_type: 'task_succeeded', task_outcome: taskOutcome })
          }
        }

        clearLastSend()
        resetStreamTracking()
      },
      onError: (error, variables, context) => {
        // Undone requests are handled by undoSend — skip toast and rollback.
        // A cancel is not a failure, and there is no cancel event type yet, so no telemetry either.
        if (undoControllerRef.current?.signal.aborted) {
          resetStreamTracking()
          return
        }

        sendTelemetryEvent({
          event_type: 'task_failed',
          task_id: variables.telemetryTask.taskId,
          task_type: variables.telemetryTask.taskType,
          model_version: variables.model,
          failure_type: failureTypeFromError(error),
          duration_ms: Date.now() - variables.startedAt,
        })

        // Restore review mode, system prompt and comments badge so the user can retry
        // without re-enabling them
        if (variables.reviewMode) {
          setReviewMode(variables.reviewMode)
        }
        if (variables.systemPrompt) {
          setSystemPrompt(variables.systemPrompt)
        }
        if (variables.includeComments) {
          setIncludeComments(variables.includeComments)
        }

        if (variables.requiresFullDocument) {
          setRequiresFullDocument(variables.requiresFullDocument)
        }

        if (context?.messageId && (!STREAMING_ENABLED || !assistantMessageId.current)) {
          deleteMessage(context.messageId)
        }

        clearLastSend()
        resetStreamTracking()
        console.error('Failed to send message:', error)
        notify({ isSuccess: false, text: t('chat.sendFailure') })
      },
    }
  )

  const sendMessage = (
    content: string,
    model: ModelKey = selectedModel,
    onSendFailed?: () => void
  ) => {
    // Snapshot captured synchronously here - before setDraftMessage('') below resets
    // reviewMode/systemPrompt (it treats an empty draft as "cleared"),
    // and before onMutate adds the optimistic user entry - so mutationFn always
    // receives a clean history without the current message, regardless of re-renders
    // triggered by state updates.
    const currentReviewMode = reviewMode
    const currentIsCommentMode = isCommentMode
    const currentIncludeComments = includeComments
    const currentSystemPrompt = systemPrompt
    const currentRequiresFullDocument = requiresFullDocumentRef.current
    if (!keepMessageOnSend) {
      setDraftMessage('')
    }
    const historySnapshot: ApiChatMessage[] = messages
      .filter(
        (message): message is typeof message & { content: string } =>
          typeof message.content === 'string'
      )
      .map(({ role, content: msgContent }) => ({ role, content: msgContent }))

    if (mutation.isPending) return

    // Known before the request: comment mode or a review prompt means Scribbler will try to
    // write comments into Word, which is what makes it a review task.
    const isReviewTask = Boolean(currentReviewMode) || currentIsCommentMode
    const telemetryTask: TelemetryTask = {
      taskId: crypto.randomUUID(),
      taskType: isReviewTask ? 'review' : 'chat',
    }

    mutation.mutate(
      {
        content,
        model,
        reviewMode: currentReviewMode,
        isCommentMode: currentIsCommentMode,
        includeComments: currentIncludeComments,
        systemPrompt: currentSystemPrompt,
        requiresFullDocument: currentRequiresFullDocument,
        historySnapshot,
        telemetryTask,
        startedAt: Date.now(),
      },
      {
        onError: onSendFailed
          ? () => {
              if (!undoControllerRef.current?.signal.aborted) onSendFailed()
            }
          : undefined,
      }
    )
  }

  /**
   * Aborts the in-flight request, removes the optimistic user message,
   * and returns its content so the caller can move it back into the editor.
   */
  const undoSend = useCallback((): string | null => {
    if (!mutation.isPending || !lastSendRef.current) return null

    const { content, messageId, reviewMode, systemPrompt, requiresFullDocument } =
      lastSendRef.current
    streamCancelledRef.current = true
    undoControllerRef.current?.abort()
    deleteMessage(messageId)
    clearLastSend()

    if (assistantMessageId.current) {
      deleteMessage(assistantMessageId.current)
      assistantMessageId.current = null
    }

    // Restore the values that setDraftMessage('') cleared on send so the user can re-send without re-configuring.
    // selectedModel and isCommentMode are not touched by setDraftMessage, so they survive as-is.
    setReviewMode(reviewMode)
    setSystemPrompt(systemPrompt)
    setRequiresFullDocument(requiresFullDocument)

    return content
  }, [
    mutation.isPending,
    deleteMessage,
    clearLastSend,
    setReviewMode,
    setSystemPrompt,
    setRequiresFullDocument,
  ])

  // Resets chat-session UI flags to the same defaults as initial hook state.
  // Call after undoSend so abort restore cannot leave dirty review/prompt/full-document flags.
  const resetChatSession = useCallback(() => {
    setIncludeComments(false)
    setIsCommentMode(false)
    setSelectedModel(DEFAULT_MODEL)
    setReviewMode(null)
    setSystemPrompt('')
    setDraftMessage('')
    setRequiresFullDocument(false)
    setIsContextBarExpanded(true)
  }, [setDraftMessage, setRequiresFullDocument])

  return {
    messages,
    isLoading: mutation.isPending,
    selectedModel,
    reviewMode,
    setReviewMode,
    isCommentMode,
    setIsCommentMode,
    includeComments,
    setIncludeComments,
    isContextBarExpanded,
    setIsContextBarExpanded,
    systemPrompt,
    setSystemPrompt,
    setSelectedModel,
    draftMessage,
    setDraftMessage,
    setRequiresFullDocument,
    sendMessage,
    undoSend,
    resetChatSession,
    deleteMessage,
    clearMessages,
    clearAgentSession: () => undefined,
    keepMessageOnSend,
    setKeepMessageOnSend,
  }
}
